import { Link } from "react-router-dom";

import { NotificationBell } from "./NotificationBell";
import { AvatarMenu } from "./AvatarMenu";
import { useNotificationBell } from "./useNotificationBell";
import { BrandLockup } from "@/components/brand/BrandLockup";
import { useAuth } from "@/lib/auth/AuthProvider";
import { avatarUrl } from "@/lib/profile/api";

/**
 * MobileHeader — the top strip: wordmark, notification bell, avatar.
 *
 * ### F28 — exactly three components, and none of them invented
 *
 * The three are all real and all global: the wordmark is the app's only entry to
 * `/dashboard`, the bell is a notification entry on every screen, and the avatar
 * is the only entry to `/profile` on mobile. Nothing route-scoped lives here —
 * `新增` stays in the `PageHero` trailing slot and the calendar's `篩選` stays
 * with `‹ 今天 ›` in `CalendarMonthNav`, because a control that is on some
 * screens and not others stops the row being the one invariant line in 39 routes.
 *
 * The avatar is what F26/F35 are downstream of. It used to be a 60px island of
 * its own in the bottom nav, and ADR-0019's reason for that was that it had to
 * be "the only route to /profile" — which is satisfied by construction once the
 * avatar is global. See ADR-0108 for why the old ADR's conclusion does not carry
 * over silently.
 *
 * ### F18 — no background, no rule
 *
 * The reference's AppShell is three rows on **one** page background: no topbar
 * fill, no `border-bottom`, no rule under the title block. The measured symptom
 * of having them was a hard edge at `y≈88` on all six `w42-capture-*.png` shots,
 * plus a second rule under the wishes subtitle — the title was reading as a
 * separate surface from the row above it.
 *
 * ### W45.0 `ADR-0111` — the `sticky` note above, resolved
 *
 * W42 left 「`sticky` stays」 with the reasoning that dropping it would be a
 * structural change to all 39 routes and that F18 was about surface treatment,
 * not about whether the row scrolls away. **That reasoning held only while the
 * header was not the sticky row** — it was `PageHero` that was sticky, and the
 * two rows shared a `top-0` offset. This round keeps exactly one sticky row and
 * gives it a gradient material that F18 could not have had.
 */
export function MobileHeader() {
  const bell = useNotificationBell();
  const { user } = useAuth();

  // Same resolution order as the old bottom island: `avatar_url` first,
  // `avatar_path` as the legacy fallback, and `avatarUrl()` still rejects a bare
  // legacy filename that has no route to be served from.
  const avatarSrc = avatarUrl(user?.avatar_url) ?? avatarUrl(user?.avatar_path);
  const avatarLabel = user?.display_name?.trim() || "我";

  return (
    // The header **is** the sticky row — there is no inner wrapper.
    //
    // The first attempt put `sticky` on a nested `.shell-bar` div, and the live
    // screenshot pass caught it: `.shell-bar` reported `top = -460` after a 460px
    // scroll, i.e. it rode away with the page. A sticky element is offset
    // relative to the scrollport but **bounded by its parent's content box**, and
    // the parent here is a 44px header — so it had 44px of travel and then left.
    //
    // (This is the same limit the W45 mockup hit, where `.shell-bar` had to go
    // through `display: contents` to shed its wrapper. The build does not need
    // that trick: `MobileShell` is `min-h-[100dvh]`, so making the header itself
    // the sticky box gives it the whole page to travel through. The unit tests
    // could not see this — jsdom reports 0 for every box.)
    <header
      data-component="MobileHeader"
      // W45.0 `ADR-0111`. Three things changed and they are one change:
      //
      // 1. `PageHero` lost its own `sticky`. Two elements sharing one `top-0`
      //    offset is not "one sticky bar" — it is two elements competing for the
      //    same 44px band, and the lower-z one slides underneath. The reported
      //    symptom (「首頁」 over the avatar and the bell) was exactly that.
      // 2. This row therefore needs a surface, because F18 removed the topbar's
      //    background back when the header scrolled away with the page. It gets
      //    one **only once content passes under it** — `.shell-bar::after`,
      //    driven by the shell's single `data-scrolled`.
      // 3. The negative margin + padding are the full-bleed: `.shell-bar::after`
      //    extends to both screen edges while the controls stay on the 16px
      //    gutter, so the fill does not need a second element to bleed through.
      //
      // W47.0 — `min-h-11` replaces `h-11`, and that single word is the fix for
      // two separate defects that looked like one. `h-11` is a **fixed** 44px and
      // `safe-top` is **padding**; Tailwind v3's preflight sets
      // `box-sizing: border-box` and nothing in this stylesheet overrides it, so
      // the padding came out of the 44px. Measured in a mobile browser:
      // `env(safe-area-inset-top)` resolves to 0px — the page viewport is already
      // inset by the browser's own URL bar even with `viewport-fit=cover` — so the
      // row got zero breathing room and the avatar and bell sat on the first pixel
      // row. On a notched PWA the inset is ~47px and the content box went
      // negative, deforming the 44px touch target. `min-h` pushes the row down
      // instead of crushing it.
      //
      // W47.3 — **the height is `min-h-*` plus the safe area, and the content is
      // centred inside it.** The previous version took that extra 8px as
      // `padding-top`, which shifts the *content box* and therefore the centre
      // with it: measured, the controls sat **4px low** at rest while being
      // exactly centred once scrolled. The user asked for the wordmark, the bell
      // and the avatar to be vertically centred **in both states**. Adding height
      // around the content and pushing it down from the top are different
      // operations, and only the first one is centring.
      //
      // Both heights now live in `globals.css` — `.shell-bar` and its
      // `[data-scrolled="true"]` variant, side by side — instead of being split
      // between a utility class and a stylesheet.
      className="shell-bar sticky top-0 z-20 flex items-center justify-between px-4"
    >
      {/* R0 (W39.0): the brand mark is 47×22 — its ink box, not its target.
          `min-h-11 min-w-11` claims the full 44px header height **and** a 44px
          width, and `px-1 -mx-1` keeps the mark exactly on the header's 16px
          gutter, so the text lands where it did before and only the target grows.
          Both `min-*` classes are here so the class string itself is the 44pt
          evidence `expectTouchTarget` reads (jsdom has no layout engine —
          tests/viewport.ts). This is the one systematic fix in the batch: it
          lands on all 12 screens.

          The wordmark keeps the *element* at 44px even though F36 moved the
          buttons beside it to 38px visual / 44px `::after`. Two different
          objects: the wordmark is a text link whose box is the type, and there
          is no 38px version of a 19px wordmark that is still legible.

          W46.1 (ADR-0129) swapped the bare `heshe` text for `BrandLockup`. The
          link's accessible name is unchanged — `BrandLockup` marks the mark
          `alt=""` and keeps the wordmark as live text — so
          `touch-target-floor.test.tsx`'s `getByRole("link", { name: "heshe" })`
          still resolves to this element. The mark is `sm` (20px) against a 19px
          wordmark: sized to the type, not to the 44px target, because the 44px
          is the touch area and a 44px logo in a 44px-tall header leaves no
          optical margin. */}
      <Link
        to="/dashboard"
        data-testid="mobile-header-brand"
        className="press font-display text-h3 text-ink-1 inline-flex items-center min-h-11 min-w-11 px-1 -mx-1"
      >
        <BrandLockup size="sm" />
      </Link>
      <div className="flex items-center gap-s-2">
        <NotificationBell open={bell.open} onOpenChange={bell.onOpenChange} />
        {/* W52.0 D-6 — was a `NavLink to="/profile"`, which is one destination
            deep. 登出 — a real primary action — lived three screens away at
            `/settings/account`, so signing out of a shared phone meant reading
            a password card first. The menu keeps `/profile` as its first item
            (nothing about the old path changes) and adds 登出 as the second.

            The `NavLink`'s lit-when-active behaviour is gone with it: the panel
            opens on tap rather than navigating, so there is no longer a route
            this control can be "on". */}
        <AvatarMenu avatarSrc={avatarSrc} avatarLabel={avatarLabel} />
      </div>
    </header>
  );
}
